import React, {useCallback, useEffect} from 'react'
import {Form} from '@modifi/plugin-forms'
import {useTranslation} from '@modifi/plugin-i18n'
import {
  Row,
  Col,
  SuccessAlert,
  InfoAlert,
  H6,
  DangerAlert,
  Paragraph,
  InlineLoader,
  ButtonLink,
  PreviousIcon,
} from '@modifi/ui'
import {translationFilter} from '@modifi/plugin-onboarding'
import DirectDebitAccountFormContent from './components/direct-debit-form-content'
import {ConnectedFormFooter} from '../../../plugins/onboarding/components/form-cta-footer'
import {ROUTE_COMPANY_PROFILE_DIRECT_DEBIT_ACCOUNT_DETAILS} from '../../../routes/constants'
import {EntityFormData} from '../../../types/common'
import {CompanyUnlockRefresher} from '../../../plugins/onboarding/components/company-unlock-refresher'
import {aFetchDirectDebitBankAccounts} from '../../../data/direct-debit-account'

export type DirectDebitAccountProps = {
  externalBankAccountId?: string
  initialFormDataLoading: boolean
  initialFormData: EntityFormData
  onSubmit: (externalBankAccountId: string | undefined, changes: Record<string, unknown>) => void
  updatePending: boolean
  clearError: () => void
  pageIsVerified: boolean
  errorMessage?: string | null
  mandateStatus?: string | null
  initialize: () => void
  externalErrorMessages?: Record<string, string>
  redirect: (route: string) => void
}

const DirectDebitAccount: React.FC<DirectDebitAccountProps> = ({
  externalBankAccountId,
  initialFormDataLoading,
  initialize,
  onSubmit,
  initialFormData,
  updatePending,
  clearError,
  pageIsVerified,
  errorMessage = null,
  mandateStatus = null,
  externalErrorMessages = {},
  redirect,
}) => {
  const {t} = useTranslation('charon')

  useEffect(() => {
    initialize()
  }, [initialize])

  useEffect(
    () => () => {
      clearError()
    },
    [clearError]
  )

  const handleSubmit = useCallback(
    (changes: Record<string, unknown>) => {
      onSubmit(externalBankAccountId, changes)
    },
    [externalBankAccountId, onSubmit]
  )

  if (initialFormDataLoading) {
    return (
      <Row justifyContent="center">
        <Col auto>
          <InlineLoader />
        </Col>
      </Row>
    )
  }

  return (
    <Form
      isLoading={updatePending}
      initialValues={initialFormData.initialValues}
      defaultValues={initialFormData.defaultValues}
      onSubmit={handleSubmit}
    >
      {pageIsVerified && (
        <SuccessAlert>{t('direct-debit-account-page.fully-verified-alert')}</SuccessAlert>
      )}
      {mandateStatus &&
        translationFilter(t, `direct-debit-account-page.mandate-status.${mandateStatus}.alert`) && (
          <InfoAlert>
            {t(`direct-debit-account-page.mandate-status.${mandateStatus}.alert`)}
          </InfoAlert>
        )}
      {errorMessage && <DangerAlert>{errorMessage}</DangerAlert>}

      <Row>
        <Col>
          <ButtonLink
            data-target="direct-debit-summary-page"
            onClick={() => redirect(ROUTE_COMPANY_PROFILE_DIRECT_DEBIT_ACCOUNT_DETAILS)}
          >
            <PreviousIcon /> {t('summary-page.back-button.label')}
          </ButtonLink>
        </Col>
      </Row>

      <Row mb={40}>
        <Col>
          <H6>{t('direct-debit-account-page.heading')}</H6>
          <Paragraph>{t('direct-debit-account-page.description')}</Paragraph>
        </Col>
      </Row>

      <DirectDebitAccountFormContent
        initialFormDataLoading={initialFormDataLoading}
        errorMessage={errorMessage}
        externalErrorMessages={externalErrorMessages || {}}
      />
      <CompanyUnlockRefresher
        refetch={(): any[] => [aFetchDirectDebitBankAccounts()]}
        initialValues={initialFormData.initialValues}
        defaultValues={initialFormData.defaultValues}
      />
      <ConnectedFormFooter loading={updatePending} hasError={!!errorMessage} />
    </Form>
  )
}

export default DirectDebitAccount
